/**
 * MSR Portal — Member Lookup.
 *
 * A quick lookup by email OR member id that returns the member's status and products
 * from the CRM's cached copy of Zuora (contacts + contact_subscriptions), via the
 * SECURITY DEFINER RPC msr_member_lookup (fails closed on role — MSRs are excluded
 * from the contacts RLS, so this cannot be a client-side select). Recent activity is a
 * "Coming Soon" placeholder — we do not yet collect a per-member activity feed.
 */
import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import MsrTabs from "../../components/msr/MsrTabs";
import {
  msrMemberLookup,
  type MsrMemberLookupResult,
  type MsrMemberSubscription,
} from "../../services/msrPortal.service";
import {
  PLACEHOLDER_CARD_EXPLANATION,
  PLACEHOLDER_CARD_TITLE,
  hiddenPlaceholdersNote,
  partitionSubscriptions,
} from "../../utils/subscriptionPlaceholders";

function fmtDate(s: string | null | undefined): string {
  if (!s) {
    return "—";
  }
  const d = new Date(s);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
}

function yesNo(v: boolean | null | undefined): string {
  return v === true ? "Yes" : v === false ? "No" : "—";
}

function StatusBadge({ status }: { status: string | null | undefined }) {
  const s = (status || "").toLowerCase();
  const active = s === "active";
  return (
    <span
      className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${
        active
          ? "bg-green-100 text-green-800"
          : s
            ? "bg-gray-200 text-gray-700"
            : "bg-gray-100 text-gray-500"
      }`}
    >
      {status || "Unknown"}
    </span>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <p className="text-xs font-medium text-gray-500">{label}</p>
      <p className="text-sm text-gray-900">{children}</p>
    </div>
  );
}

function ProductChips({ names }: { names: string[] | null | undefined }) {
  if (!names || names.length === 0) {
    return <span className="text-sm text-gray-500">None on record</span>;
  }
  return (
    <div className="flex flex-wrap gap-1.5">
      {names.map((n, i) => (
        <span
          key={`${n}-${i}`}
          className="inline-flex rounded-md bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-800"
        >
          {n}
        </span>
      ))}
    </div>
  );
}

function SubscriptionCard({ sub }: { sub: MsrMemberSubscription }) {
  return (
    <div className="rounded-md border border-gray-200 p-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-sm font-medium text-gray-900">
          {sub.subscription_number || "Subscription"}
        </span>
        <StatusBadge status={sub.status} />
      </div>
      <div className="mb-2">
        <ProductChips names={sub.product_names} />
      </div>
      <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
        <span>
          Term: {fmtDate(sub.term_start_date)} – {fmtDate(sub.term_end_date)}
        </span>
        <span>Region: {sub.region || "—"}</span>
        <span>Type: {sub.subscription_type || "—"}</span>
        <span>Auto-renew: {yesNo(sub.auto_renew)}</span>
      </div>
    </div>
  );
}

function MemberResult({ m }: { m: MsrMemberLookupResult }) {
  const name = [m.first_name, m.last_name].filter(Boolean).join(" ") || "—";
  // June 2026 bulk-import placeholders (SYNTH-…, no product): hidden beside a real
  // subscription, collapsed into one explained card when they are all there is.
  const subs = partitionSubscriptions(m.subscriptions);
  return (
    <div className="space-y-4">
      {/* Identity + status */}
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="mb-3 flex items-start justify-between gap-3">
          <div>
            <p className="text-lg font-semibold text-gray-900">{name}</p>
            <p className="text-sm text-gray-600">{m.email || "—"}</p>
          </div>
          {/* Headline status is AUTHORITATIVE on has_active_subscription (matches the "Active
              subscription" field below + the EIB member definition). A stale member_status='active'
              with no active subscription reads "inactive", not a green "active"; a genuine lapsed
              label (expired/cancelled) is shown as-is. 2026-07-30. */}
          <StatusBadge
            status={
              m.has_active_subscription
                ? "active"
                : m.member_status && m.member_status.toLowerCase() !== "active"
                  ? m.member_status
                  : "inactive"
            }
          />

          {/* end status */}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Member #">{m.membership_id || "—"}</Field>
          <Field label="Contact type">{m.contact_type || "—"}</Field>
          <Field label="Tier">{m.membership_tier || "—"}</Field>
          <Field label="Type">{m.membership_type || "—"}</Field>
          <Field label="Active subscription">
            {yesNo(m.has_active_subscription)}
          </Field>
          <Field label="Paying member">{yesNo(m.paying_member)}</Field>
          <Field label="Member since">{fmtDate(m.member_start_date)}</Field>
          <Field label="Renews / expires">
            {fmtDate(m.member_renewal_date || m.member_expiration_date)}
          </Field>
        </div>
      </div>

      {/* Products */}
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <p className="mb-2 text-sm font-semibold text-gray-900">Products</p>
        <ProductChips names={m.product_names} />
        {(subs.visible.length > 0 || subs.placeholderOnly) && (
          <div className="mt-3 space-y-2">
            {subs.placeholderOnly && (
              <div
                className="rounded-md border border-gray-200 p-3"
                data-testid="msr-subscription-placeholder-card"
              >
                <div className="flex items-start justify-between gap-3">
                  <span className="text-sm font-medium text-gray-900">
                    {PLACEHOLDER_CARD_TITLE}
                  </span>
                  <StatusBadge status={subs.placeholderOnly.status} />
                </div>
                <p className="mt-2 text-xs text-gray-500">
                  {PLACEHOLDER_CARD_EXPLANATION}
                </p>
              </div>
            )}
            {subs.visible.map((s, i) => (
              <SubscriptionCard key={s.subscription_number || i} sub={s} />
            ))}
          </div>
        )}
        {subs.hiddenPlaceholders.length > 0 && (
          <p className="mt-2 text-xs text-gray-400">
            {hiddenPlaceholdersNote(subs.hiddenPlaceholders)}
          </p>
        )}
      </div>

      {/* Recent activity — Coming Soon (not yet collected) */}
      <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4">
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold text-gray-700">Recent activity</p>
          <span className="inline-flex rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-800">
            Coming Soon
          </span>
        </div>
        <p className="mt-1 text-sm text-gray-500">
          A timeline of this member's recent activity will appear here.
        </p>
      </div>
    </div>
  );
}

const MemberLookup: React.FC = () => {
  const [query, setQuery] = useState("");
  const [submitted, setSubmitted] = useState("");

  const { data, isFetching, error } = useQuery({
    queryKey: ["msr-member-lookup", submitted],
    queryFn: () => msrMemberLookup(submitted),
    enabled: submitted.trim().length > 0,
  });

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitted(query.trim());
  }

  return (
    <div className="mx-auto max-w-2xl space-y-4 p-4">
      <MsrTabs active="member-lookup" />

      <div>
        <h1 className="text-xl font-bold text-gray-900">Member Lookup</h1>
        <p className="text-sm text-gray-600">
          Look up a member by email or member number to see their status and
          products.
        </p>
      </div>

      <form onSubmit={onSubmit} className="flex gap-2">
        <input
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          placeholder="Email or member number"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoComplete="off"
        />
        <button
          type="submit"
          disabled={!query.trim()}
          className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
        >
          Search
        </button>
      </form>

      {submitted && isFetching && (
        <p className="text-sm text-gray-500">Looking up…</p>
      )}
      {error && (
        <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-800">
          Could not complete the lookup. Please try again.
        </div>
      )}
      {submitted && !isFetching && !error && data && !data.found && (
        <div className="rounded-md bg-gray-50 border border-gray-200 p-4 text-sm text-gray-600">
          No member found for “{submitted}”. Check the email or member number
          and try again.
        </div>
      )}
      {!isFetching && !error && data && data.found && <MemberResult m={data} />}
    </div>
  );
};

export default MemberLookup;
